import type { FC } from 'react';

import { useContent, useImageMapper, useSetting } from '@eneco/cms';
import { Heading, Image, Stack, Text } from '@eneco/sparky';
import type { HeadingProps } from '@eneco/sparky/types';

import type { ArticleContent } from './article-content.types';

/**
 * `Article` block for the CMS-agnostic lane, serving **both** providers. The header of an article
 * page: hero image, title, subtitle and byline.
 *
 * Three documented simplifications against the legacy component
 * (`libs/sitecore/components/content/src/Article`), all forced by the `lib:cms-components` module
 * boundary (`nx.json`) rather than chosen:
 *
 * - **No `@eneco/custom-components/Article` layout.** That lib is out of boundary, so the image and
 *   heading render as a plain stack instead of its overlapping image/heading treatment.
 * - **No tags.** The legacy component reads them from the Sitecore route's own fields via
 *   `useLayoutData`; a Sanity page carries no equivalent, so there is nothing to read.
 * - **Date formatted with `Intl`, not `@eneco/i18n`'s `useFormatter`.** `lib:i18n` is out of
 *   boundary too. The locale comes from the runtime `Intl` default locale rather than the i18n provider.
 */
const Article: FC = () => {
  const content = useContent<ArticleContent>();
  const mapImage = useImageMapper();

  const headingLevel = (useSetting('headingLevel') || 'h1') as HeadingProps['as'];
  const image = mapImage(content.image);
  const published = formatDate(content.publicationDate);
  const byline = [content.bylineText, content.author, published].filter(Boolean).join(' · ');

  return (
    <Stack gap="6">
      {image?.src && (
        <Image src={image.src} alt={image.alt ?? ''} width="100%" objectFit="cover" aspectRatio={image.aspectRatio} />
      )}
      <Stack gap="5">
        {content.title && (
          <Heading as={headingLevel} size={{ initial: 'M', md: 'XL' }}>
            {content.title}
          </Heading>
        )}
        {content.subTitle && <Text size={{ initial: 'BodyM', md: 'BodyL' }}>{content.subTitle}</Text>}
        {byline && (
          <Text size={{ initial: 'BodyM', md: 'BodyL' }} color="textLowEmphasis">
            {byline}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};

/** A migrated `publicationDate` is an ISO datetime. An unparseable one is dropped rather than shown raw. */
const formatDate = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
};

export default Article;
